import { motion } from "framer-motion";
import type React from "react";
import { useReducedMotion } from "./useReducedMotion";

export interface SlideInProps {
  /** Content to animate */
  children: React.ReactNode;
  /** Direction to slide from */
  direction: "left" | "right" | "up" | "down";
  /** Distance token, custom CSS value, or numeric pixels */
  distance?: "xs" | "sm" | "md" | string | number;
  /** Duration token or custom duration in ms */
  duration?: "instant" | "fast" | "normal" | "slow" | "slower" | number;
  /** Delay before animation starts (ms) */
  delay?: number;
  /** Additional CSS classes */
  className?: string;
}

const durationMap = {
  instant: 0,
  fast: 150,
  normal: 250,
  slow: 350,
  slower: 500,
};

const distanceMap = {
  xs: "4px",
  sm: "8px",
  md: "16px",
};

/**
 * Get the distance value as a CSS-compatible string
 */
function getDistance(distance: string | number | undefined): string {
  if (!distance) return distanceMap.sm; // Default to sm

  // Token-based distance
  if (typeof distance === "string" && distance in distanceMap) {
    return distanceMap[distance as keyof typeof distanceMap];
  }

  // Numeric distance (convert to px)
  if (typeof distance === "number") {
    return `${distance}px`;
  }

  // Custom CSS value (100%, 50vw, etc.)
  return distance;
}

/**
 * SlideIn primitive component
 * Slides and fades content in from a direction, respects prefers-reduced-motion
 */
export function SlideIn({
  children,
  direction,
  distance = "sm",
  duration = "normal",
  delay = 0,
  className,
}: SlideInProps) {
  const prefersReducedMotion = useReducedMotion();

  const durationMs = typeof duration === "number" ? duration : durationMap[duration];
  const distanceValue = getDistance(distance);

  // Map direction to transform values
  const getTransform = (dist: string) => {
    switch (direction) {
      case "left":
        return { x: `-${dist}` };
      case "right":
        return { x: dist };
      case "up":
        return { y: `-${dist}` };
      case "down":
        return { y: dist };
    }
  };

  // For reduced motion, only fade (no slide)
  const initial = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, ...getTransform(distanceValue) };

  const animate = { opacity: 1, x: 0, y: 0 };

  const exit = prefersReducedMotion ? { opacity: 0 } : { opacity: 0, ...getTransform(distanceValue) };

  return (
    <motion.div
      className={className}
      initial={initial}
      animate={animate}
      exit={exit}
      transition={{
        duration: prefersReducedMotion ? 0 : durationMs / 1000,
        delay: prefersReducedMotion ? 0 : delay / 1000,
        ease: [0.4, 0.0, 0.2, 1], // motion-easing-standard
      }}
    >
      {children}
    </motion.div>
  );
}
